/**
 * Utility functions for date and time operations with proper TypeScript types
 * Enhanced with date-fns for better reliability and timezone handling
 */

import { 
  startOfWeek, 
  addDays, 
  format, 
  isSameDay as dateFnsIsSameDay, 
  isPast as dateFnsIsPast,
  parseISO,
  addWeeks,
  subWeeks,
  getMonth,
  getYear,
  isToday as dateFnsIsToday
} from 'date-fns'

export interface DateComponents {
  year: number
  month: number
  date: number
}

export interface TimeComponents {
  hours: number
  minutes: number
  seconds?: number
}

export interface DateTimeComponents extends DateComponents, TimeComponents {}

/**
 * Create a Date object from components with proper timezone handling
 */
export function createDateFromComponents(components: DateComponents, timeComponents?: TimeComponents): Date {
  const { year, month, date } = components
  
  if (timeComponents) {
    const { hours, minutes, seconds = 0 } = timeComponents
    return new Date(year, month - 1, date, hours, minutes, seconds, 0)
  }
  
  return new Date(year, month - 1, date)
}

/**
 * Parse a date string in YYYY-MM-DD format to DateComponents
 */
export function parseDateString(dateString: string): DateComponents {
  const [year, month, date] = dateString.split('-').map(Number)
  return { year, month, date }
}

/**
 * Parse a time string in HH:MM format to TimeComponents
 */
export function parseTimeString(timeString: string): TimeComponents {
  const [hours, minutes] = timeString.split(':').map(Number)
  return { hours, minutes }
}

/**
 * Format a Date object to YYYY-MM-DD string
 */
export function formatDateToString(date: Date): string {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a Date object to HH:MM string
 */
export function formatTimeToString(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Check if a date is today (enhanced with date-fns)
 */
export function isToday(date: Date): boolean {
  return dateFnsIsToday(date)
}

/**
 * Check if a date is in the past (enhanced with date-fns)
 */
export function isPastDate(date: Date): boolean {
  return dateFnsIsPast(date)
}

/**
 * Check if a time is in the past for today
 */
export function isPastTimeForToday(date: Date, timeComponents: TimeComponents): boolean {
  if (!isToday(date)) return false
  
  const now = new Date()
  const currentHour = now.getHours()
  return timeComponents.hours < currentHour
}

/**
 * Add hours to a time string and return the new time string
 */
export function addHoursToTimeString(timeString: string, hoursToAdd: number): string {
  const { hours, minutes } = parseTimeString(timeString)
  const newHours = (hours + hoursToAdd) % 24
  return `${newHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Parse time string like "2:00 PM" to military time "14:00"
 */
export function parseTimeToMilitary(timeStr: string): string {
  const [time, period] = timeStr.split(' ')
  const [hours, minutes] = time.split(':').map(Number)
  
  let militaryHours = hours
  if (period === 'PM' && hours !== 12) {
    militaryHours += 12
  } else if (period === 'AM' && hours === 12) {
    militaryHours = 0
  }
  
  return `${militaryHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * Format hour and minute to display time with AM/PM
 */
export function formatTimeDisplay(hour: number, minute: number = 0): string {
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  const displayMinute = minute.toString().padStart(2, '0')
  
  return `${displayHour}:${displayMinute} ${period}`
}

/**
 * Calculate duration between two times in minutes
 */
export function calculateDurationMinutes(startTime: string, endTime: string): number {
  const start = parseTimeString(startTime)
  const end = parseTimeString(endTime)
  
  const startMinutes = start.hours * 60 + start.minutes
  const endMinutes = end.hours * 60 + end.minutes
  
  return endMinutes - startMinutes
}

/**
 * Format duration in minutes to human-readable string
 */
export function formatDuration(durationMinutes: number): string {
  if (durationMinutes >= 60) {
    const hours = Math.floor(durationMinutes / 60)
    const minutes = durationMinutes % 60
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`.trim()
  }
  return `${durationMinutes}m`
}

/**
 * Get the start of the week (Sunday) for a given date (enhanced with date-fns)
 */
export function getStartOfWeek(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 0 }) // Sunday = 0
}

/**
 * Add weeks to a date (enhanced with date-fns)
 */
export function addWeeksToDate(date: Date, weeks: number): Date {
  return addWeeks(date, weeks)
}

/**
 * Subtract weeks from a date (enhanced with date-fns)
 */
export function subtractWeeksFromDate(date: Date, weeks: number): Date {
  return subWeeks(date, weeks)
}

/**
 * Get all days in a week starting from Sunday (enhanced with date-fns)
 */
export function getWeekDays(startOfWeek: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(startOfWeek, i))
}

/**
 * Format date for display with consistent timezone (enhanced with date-fns)
 */
export function formatDateForDisplay(date: Date, formatStr: string = 'MMM d, yyyy'): string {
  return format(date, formatStr)
}

/**
 * Format time for display (enhanced with date-fns)
 */
export function formatTimeForDisplay(date: Date, formatStr: string = 'h:mm a'): string {
  return format(date, formatStr)
}

/**
 * Check if two dates are the same day (enhanced with date-fns)
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return dateFnsIsSameDay(date1, date2)
}

/**
 * Create a date with Philippines timezone consideration
 */
export function createPhilippinesDate(year: number, month: number, date: number, hours: number = 0, minutes: number = 0): Date {
  // Create date in local timezone, then adjust for Philippines (GMT+8)
  const localDate = new Date(year, month - 1, date, hours, minutes)
  
  // Get timezone offset for Philippines (UTC+8)
  const philippinesOffset = 8 * 60 // 8 hours in minutes
  const localOffset = localDate.getTimezoneOffset()
  const offsetDiff = philippinesOffset + localOffset
  
  return new Date(localDate.getTime() + (offsetDiff * 60 * 1000))
}